import React, { useState } from 'react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Lightbox from '../../components/ui/Lightbox';
import sportData from '../../data/sportData.json';
import { getCleanImageUrl } from '../../utils/imageHelper';
import {
  TrophyIcon,
  HeartIcon,
  UserGroupIcon,
  SparklesIcon,
  CameraIcon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

export default function ActivitesSportivesPage() {
  const [selectedMedia, setSelectedMedia] = useState(null);

  return (
    <div className="max-w-6xl mx-auto space-y-12 animate-fade-in px-4">
      {/* Hero */}
      <Card variant="elevated" className="p-6 sm:p-10 flex flex-col md:flex-row items-center gap-8">
        <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-full overflow-hidden border-4 border-white shadow-acem-md flex-shrink-0 bg-acem-surface-container-low relative">
          <img
            src={getCleanImageUrl(sportData.imageHero)}
            alt="Activités Sportives ACEM Fès"
            className="w-full h-full object-cover object-bottom"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/logo.png";
              e.currentTarget.className = "w-20 h-20 object-contain m-auto opacity-40";
            }}
          />
        </div>
        <div className="flex-1 space-y-3 text-center md:text-left">
          <Badge variant="success">ACEM Fès Sport & Santé</Badge>
          <h1 className="font-title text-2xl sm:text-4xl font-extrabold text-acem-primary-dark">
            Activités Sportives
          </h1>
          <p className="text-xs sm:text-sm text-acem-text/85 leading-relaxed">
            {sportData.descriptionHero}
          </p>
        </div>
      </Card>

      {/* Objectifs */}
      <section className="space-y-4">
        <div className="border-b border-acem-border pb-3">
          <h2 className="font-title text-xl font-bold text-acem-primary-dark flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-acem-primary/10 border border-acem-primary/20 flex items-center justify-center text-acem-primary">
              <TrophyIcon className="w-5 h-5" aria-hidden="true" />
            </div>
            <span>Nos Objectifs Sportifs</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {sportData.objectifs?.map((obj, idx) => {
            const colors = [
              "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
              "bg-blue-500/10 text-blue-600 border-blue-500/20",
              "bg-amber-500/10 text-amber-600 border-amber-500/20",
              "bg-purple-500/10 text-purple-600 border-purple-500/20",
            ];
            const badgeColor = colors[idx % colors.length];

            const objectiveIcons = [
              <HeartIcon className="w-6 h-6" aria-hidden="true" />,
              <UserGroupIcon className="w-6 h-6" aria-hidden="true" />,
              <TrophyIcon className="w-6 h-6" aria-hidden="true" />,
              <SparklesIcon className="w-6 h-6" aria-hidden="true" />,
            ];

            return (
              <Card key={idx} variant="default" className="p-5 text-center space-y-3 flex flex-col items-center">
                <div className={`w-12 h-12 rounded-2xl border ${badgeColor} flex items-center justify-center shadow-sm`}>
                  {objectiveIcons[idx % objectiveIcons.length]}
                </div>
                <h3 className="font-bold text-sm text-acem-text">{obj.titre}</h3>
                <p className="text-xs text-acem-muted leading-relaxed">{obj.description}</p>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Sports Réguliers */}
      <section className="space-y-4">
        <div className="border-b border-acem-border pb-3">
          <h2 className="font-title text-xl font-bold text-acem-primary-dark flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-acem-primary/10 border border-acem-primary/20 flex items-center justify-center text-acem-primary">
              <ClockIcon className="w-5 h-5" aria-hidden="true" />
            </div>
            <span>Sports Réguliers & Entraînements</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {sportData.sportsReguliers?.map((sport, idx) => (
            <Card key={idx} variant="elevated" className="p-6 text-center space-y-3 group">
              <div className="w-16 h-16 mx-auto mb-2 rounded-2xl bg-acem-surface-container-low border border-acem-border p-3 flex items-center justify-center shadow-acem-sm group-hover:scale-110 transition-transform duration-300">
                {sport.icone && (sport.icone.startsWith('/') || sport.icone.endsWith('.svg') || sport.icone.endsWith('.png')) ? (
                  <img
                    src={sport.icone}
                    alt={sport.titre}
                    className="w-full h-full object-contain filter drop-shadow-sm"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/logo.png";
                    }}
                  />
                ) : (
                  <span className="text-3xl">{sport.icone}</span>
                )}
              </div>
              <h3 className="font-title text-lg font-bold text-acem-primary-dark">{sport.titre}</h3>
              <p className="text-xs sm:text-sm text-acem-muted leading-relaxed">{sport.description}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Tournois & Événements majeurs */}
      <section className="space-y-6">
        <div className="border-b border-acem-border pb-3">
          <h2 className="font-title text-xl sm:text-2xl font-bold text-acem-primary-dark flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
              <TrophyIcon className="w-5 h-5" aria-hidden="true" />
            </div>
            <span>Tournois & Compétitions</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {sportData.tournois?.map((t, idx) => {
            const cleanTournoiImg = getCleanImageUrl(t.image);
            return (
              <Card key={idx} variant="elevated" className="p-6 space-y-4">
                <div
                  onClick={() => setSelectedMedia(cleanTournoiImg)}
                  className="w-full h-64 rounded-xl overflow-hidden bg-acem-surface-container-low cursor-pointer group shadow-sm flex items-center justify-center relative"
                >
                  <img
                    src={cleanTournoiImg}
                    alt={t.titre}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/logo.png";
                      e.currentTarget.className = "w-20 h-20 object-contain m-auto opacity-40";
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-title text-lg font-bold text-acem-primary-dark">{t.titre}</h3>
                    <Badge variant="primary">{t.date}</Badge>
                  </div>
                  <p className="text-xs sm:text-sm text-acem-text/80 leading-relaxed">{t.description}</p>
                </div>

                {/* Galerie médias miniatures */}
                {t.galerie && t.galerie.length > 0 && (
                  <div className="pt-2 border-t border-acem-border/50">
                    <h4 className="text-xs font-bold text-acem-muted mb-2 flex items-center gap-1.5">
                      <CameraIcon className="w-3.5 h-3.5 text-acem-primary" aria-hidden="true" />
                      Galerie de la compétition
                    </h4>
                    <div className="grid grid-cols-4 gap-2">
                      {t.galerie.map((imgUrl, gIdx) => {
                        const cleanGImg = getCleanImageUrl(imgUrl);
                        return (
                          <div
                            key={gIdx}
                            onClick={() => setSelectedMedia(cleanGImg)}
                            className="h-16 rounded-lg overflow-hidden bg-acem-surface-container-low cursor-pointer border border-acem-border hover:opacity-90 flex items-center justify-center"
                          >
                            <img
                              src={cleanGImg}
                              alt={`${t.titre} ${gIdx + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = "/logo.png";
                                e.currentTarget.className = "w-8 h-8 object-contain m-auto opacity-40";
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </section>

      {/* Palmarès */}
      {sportData.palmares && sportData.palmares.length > 0 && (
        <section className="space-y-4">
          <div className="border-b border-acem-border pb-3">
            <h2 className="font-title text-xl font-bold text-acem-primary-dark flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="15" r="5" strokeDasharray="4 2" />
                  <path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.12" />
                  <path d="M12 7a8 8 0 1 0 0 16 8 8 0 0 0 0-16z" />
                </svg>
              </div>
              <span>Notre Palmarès</span>
            </h2>
          </div>

          <div className="space-y-3">
            {sportData.palmares.map((item, idx) => (
              <Card key={idx} variant="elevated" className="p-4 flex items-center gap-4 sm:gap-6">
                <span className="font-title text-2xl font-extrabold text-acem-primary w-16 text-center flex-shrink-0">
                  {item.annee}
                </span>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-bold text-sm sm:text-base text-acem-text">{item.competition}</h4>
                    <Badge variant="accent">{item.resultat}</Badge>
                  </div>
                  <p className="text-xs text-acem-muted">{item.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Lightbox */}
      {selectedMedia && (
        <Lightbox
          src={selectedMedia}
          onClose={() => setSelectedMedia(null)}
        />
      )}
    </div>
  );
}
